/**
 * Every procedure the dashboard calls, named once.
 *
 * The path strings are the one thing in this arrangement that no compiler checks (see
 * `client.ts`'s header), so they live in a single file rather than being scattered across
 * the screens that call them — a wrong path is then one edit away from being right, and
 * `grep` over this file is the complete list of what the SPA asks the server for.
 *
 * Each entry pairs a path with the schema in `packages/shared` that the op's return type
 * is inferred from. Adding a procedure means adding a line here and a schema there, which
 * is R7's *"one schema, one op, two thin mounts"* arriving at the consuming end.
 */
import {
  artworkBatchRequestSchema,
  artworkPreflightSchema,
  auditLogListSchema,
  auditLogQuerySchema,
  batchListSchema,
  billingContactSchema,
  setBillingContactSchema,
  type SetBillingContact,
  claimCompanyPrefixSchema,
  companyPrefixClaimedSchema,
  companyPrefixListSchema,
  prefixEvidenceSubmittedSchema,
  submitPrefixEvidenceProcedureSchema,
  type ClaimCompanyPrefixInput,
  createLinkSchema,
  createOrgSchema,
  createProductSchema,
  createDomainSchema,
  checkoutRequestSchema,
  checkoutResponseSchema,
  currentOrgSchema,
  subscriptionViewSchema,
  switchPreviewQuerySchema,
  switchPreviewSchema,
  switchRequestSchema,
  switchResponseSchema,
  manageRequestSchema,
  manageResponseSchema,
  endTrialResponseSchema,
  hardCapSchema,
  type HardCap,
  type ManageRequest,
  usageViewSchema,
  type CheckoutRequest,
  type SwitchRequest,
  domainCreatedSchema,
  domainListSchema,
  domainRecheckedSchema,
  domainRemovedSchema,
  linkActiveSchema,
  linkCreatedSchema,
  linkDeletedSchema,
  linkListSchema,
  linkOrderSchema,
  linkTypeListSchema,
  linkUpdatedSchema,
  linksetPreviewQuerySchema,
  linksetPreviewSchema,
  mediaListSchema,
  productCreatedSchema,
  membershipSummarySchema,
  myOrgsSchema,
  productCountsSchema,
  productListQuerySchema,
  productListSchema,
  productStatusSchema,
  productSummarySchema,
  recallDetailSchema,
  recallLiftedSchema,
  recallListSchema,
  recallPublishedSchema,
  recallWidenedSchema,
  updateLinkSchema,
  variantListSchema,
  type ArtworkBatchRequest,
  type AuditLogQuery,
  type CreateLinkInput,
  type CreateOrgInput,
  type CreateProductInput,
  type LinksetPreviewQuery,
  type MembershipSummary,
  type ProductListQuery,
  type UpdateLinkInput,
  createInviteSchema,
  inviteAcceptedSchema,
  inviteListSchema,
  inviteRevokedSchema,
  inviteSummarySchema,
  memberListSchema,
  memberRemovedSchema,
  memberRoleSetSchema,
  ownershipTransferredSchema,
  setMemberRoleSchema,
  type Role,
  type SetMemberRoleInput,
  applyImportSchema,
  confirmImportMappingSchema,
  createImportSchema,
  importDetailSchema,
  importIdSchema,
  importListQuerySchema,
  importPageSchema,
  importReportQuerySchema,
  importReportSchema,
  importRowsPageSchema,
  importRowsQuerySchema,
  importUploadSchema,
  importViewSchema,
  type ApplyImportInput,
  type ConfirmImportMappingInput,
  type CreateImportInput,
  type ImportRowsQuery,
} from "@packagepage/shared";

import { mutate, query } from "./client.js";

export const api = {
  /**
   * The caller's memberships. The query that decides what the org header may be set to,
   * and therefore the only one that is safe to call before the header exists.
   */
  myOrgs: () => query("orgs.mine", myOrgsSchema),

  /**
   * The bootstrap.
   *
   * `createOrgSchema.parse` here is not a second rule — it is the same schema the
   * procedure validates with, applied earlier so the form can refuse in the server's own
   * words instead of round-tripping to hear them.
   */
  createOrg: (input: CreateOrgInput): Promise<MembershipSummary> =>
    mutate("orgs.create", createOrgSchema.parse(input), membershipSummarySchema),

  recalls: () => query("recalls.list", recallListSchema),

  /**
   * The organization's own audit trail, newest first.
   *
   * Ungated — U2's ruling put the surface in Phase 2 and the recommendation was to ship it
   * without a tier check, since there are no paying tiers until Phase 5. The matrix answers
   * `read`/`auditLog` for every role, so no screen has to decide who may look.
   */
  auditLog: (input: AuditLogQuery) =>
    query("auditLog.list", auditLogListSchema, auditLogQuerySchema.parse(input)),
  companyPrefixes: () => query("companyPrefixes.list", companyPrefixListSchema),

  /** Lands `unverified`. A prefix another organisation holds is refused `PREFIX_DISPUTED`. */
  claimPrefix: (input: ClaimCompanyPrefixInput) =>
    mutate(
      "companyPrefixes.claim",
      claimCompanyPrefixSchema.parse(input),
      companyPrefixClaimedSchema,
    ),

  /** Files the document and returns a presigned PUT. The bytes go to the bucket, never here. */
  submitPrefixEvidence: (input: {
    prefixId: string;
    mime: string;
    bytes: number;
    filename?: string;
  }) =>
    mutate(
      "companyPrefixes.submitEvidence",
      submitPrefixEvidenceProcedureSchema.parse(input),
      prefixEvidenceSubmittedSchema,
    ),

  /**
   * One page of the catalogue.
   *
   * The input is parsed with `productListQuerySchema` **here, on the way out**, so the
   * defaults the server would apply (`limit` 100, `sort` createdAt, `order` desc) are
   * applied by the same definition before the request is made. Without that the screen
   * would hold one set of defaults, the server another, and the cursor — which encodes
   * the ordering it was issued under — would be refused on the second page for a
   * mismatch neither side authored.
   */
  products: (input: Partial<ProductListQuery> = {}) =>
    query("products.list", productListSchema, productListQuerySchema.parse(input)),

  /**
   * Catalogue totals per status.
   *
   * A separate call from `products`, deliberately: the list returns a **page** and this
   * returns the **catalogue**. c34 paginated the list and briefly left the dashboard
   * counting statuses out of the first hundred rows — a number that was present,
   * plausible and too small. Two questions, two calls, and neither derived from the
   * other.
   */
  productCounts: () => query("products.counts", productCountsSchema),

  /**
   * The organization being acted in, and where its codes answer.
   *
   * Separate from {@link api.myOrgs} because it answers a different question: `mine` is the
   * list the org header may be set from, and this is the settled organization's own
   * context — including `resolverBaseUrl`, which the browser cannot read for itself and
   * which *"View live page"* must have to open exactly what a scan gets.
   */
  currentOrg: () => query("orgs.current", currentOrgSchema),

  /** Owner and billing roles only — the matrix refuses the rest, and the screen omits its line. */
  subscription: () => query("subscription.current", subscriptionViewSchema),

  /** The current window's usage counters. Every role — the dashboard's usage check reads it. */
  usage: () => query("usage.current", usageViewSchema),

  /** A first purchase. Answers Stripe's hosted page, which the screen navigates to. */
  checkout: (input: CheckoutRequest) =>
    mutate(
      "subscription.checkout",
      checkoutRequestSchema.parse(input),
      checkoutResponseSchema,
    ),

  /** What moving to `tier` costs, or `null` when Stripe cannot say. */
  switchPreview: (input: SwitchRequest) =>
    query(
      "subscription.preview",
      switchPreviewSchema,
      switchPreviewQuerySchema.parse(input),
    ),

  /** A switch. Answers Stripe's hosted plan-change page, which the screen navigates to. */
  switchPlan: (input: SwitchRequest) =>
    mutate("subscription.switch", switchRequestSchema.parse(input), switchResponseSchema),

  /** Stripe's portal, to cancel at period end or to update the card behind a failed payment. */
  manageBilling: (input: ManageRequest) =>
    mutate("subscription.manage", manageRequestSchema.parse(input), manageResponseSchema),

  /** "Let it lapse": ends the trial now, and the trial clock moves the plan. */
  endTrial: () => mutate("subscription.endTrial", undefined, endTrialResponseSchema),

  /** The hard cap on active GTINs. Read back through `orgs.current`. */
  setHardCap: (input: HardCap) =>
    mutate("subscription.setHardCap", hardCapSchema.parse(input), hardCapSchema),

  /**
   * Where Stripe sends invoices. Its own query and not a field on `subscription`: it costs
   * one Stripe call per render, so an unreachable Stripe fails this row and not the screen.
   */
  billingContact: () => query("subscription.contact", billingContactSchema),
  setBillingContact: (input: SetBillingContact) =>
    mutate(
      "subscription.setContact",
      setBillingContactSchema.parse(input),
      billingContactSchema,
    ),

  /**
   * Create a product. Always lands `draft` — publishing is a separate act.
   *
   * `createProductSchema.parse` here is the same schema the procedure validates with,
   * applied earlier so the form can refuse in the server's own words instead of
   * round-tripping to hear them. One definition, checked twice.
   */
  createProduct: (input: CreateProductInput) =>
    mutate("products.create", createProductSchema.parse(input), productCreatedSchema),

  /** One product. The detail screen's header reads this and nothing else. */
  product: (id: string) => query("products.read", productSummarySchema, { id }),

  variants: (productId: string) =>
    query("variants.list", variantListSchema, { productId }),
  batches: (productId: string) => query("batches.list", batchListSchema, { productId }),
  media: (productId: string) => query("media.list", mediaListSchema, { productId }),

  /**
   * Publish — the act that makes a code answer.
   *
   * Answers with the status **the server ended up in**, not the one the caller asked for.
   * The op can refuse mid-flight (the GTIN is claimed elsewhere; nothing holds the default
   * slot), so a screen that assumed `published` would render a success over a refusal.
   */
  publishProduct: (id: string) => mutate("products.publish", { id }, productStatusSchema),

  /**
   * Retire — archive, or permanently.
   *
   * `to` is required and has no default here, deliberately: the REST route treats anything
   * that is not `"tombstoned"` as `"archived"`, and a permanent act reached by *omitting* a
   * parameter is the wrong shape for the one call in this file a user cannot undo.
   */
  retireProduct: (id: string, to: "archived" | "tombstoned") =>
    mutate("products.retire", { id, to }, productStatusSchema),

  /**
   * The organisation's custom domains, and the CNAME every one of them needs.
   *
   * `dns` rides on the LIST rather than being a constant in this bundle: `EDGE_CNAME_TARGET`
   * is `packages/shared`'s, and a second spelling in the SPA is the failure
   * `apps/api/src/urls.ts`'s header is written about. The server says what to publish.
   */
  domains: () => query("domains.list", domainListSchema),

  /**
   * Claim a hostname.
   *
   * The response's `warnings` array is **present and empty** rather than absent (§5), and
   * carries `DOMAIN_APEX_STEERED` when the hostname is a whole domain — the create SUCCEEDS
   * and the screen steers, because §8.1 blocks only what is unrecoverable after printing.
   */
  createDomain: (input: unknown) =>
    mutate("domains.create", createDomainSchema.parse(input), domainCreatedSchema),

  /**
   * Take a hostname out of service.
   *
   * Named `remove`, like `links.remove`, and it deletes no row: `0023` makes removal a
   * status so `first_live_at` survives and re-adding the hostname revives it. The response
   * counts the entries the removal re-emitted — `0016`'s org-wide obligation, as a number
   * the screen could show rather than a promise it makes.
   */
  removeDomain: (id: string) => mutate("domains.remove", { id }, domainRemovedSchema),

  /**
   * Ask DNS again, now.
   *
   * A mutation, not a query: it moves `status`, `last_dns_answer` and `last_checked_at`.
   * `answer` is what the lookup returned and is `null` both when no record exists and when
   * the lookup itself failed — the two are distinguished server-side, and the screen must
   * not report the second as the first.
   */
  recheckDomain: (id: string) => mutate("domains.recheck", { id }, domainRecheckedSchema),

  /**
   * §8.1's gates, evaluated for one product against one set of settings — **before**
   * anything is generated.
   *
   * The screen's whole information source for what it may say. `POST /v1/artwork` answers
   * with a file and projects its advisories into headers carrying codes only, so the prose
   * a person reads — the refusal's `detail`, its `remedy.label`, §8.3's disclosure — lives
   * here, and U17's rule follows from that rather than being imposed on top of it: the
   * screen has nowhere to get a refusal sentence except the body.
   *
   * `artworkBatchRequestSchema.parse` on the way out, like every other call in this file:
   * `applicationPreset` has a default and it belongs to one definition. A single-element
   * `productIds` is not a degenerate batch — §8.1 requires the batch and the single call to
   * answer identically, and asking the batch about one product is how this screen holds the
   * server to that.
   */
  artworkPreflight: (input: ArtworkBatchRequest) =>
    query(
      "artwork.preflight",
      artworkPreflightSchema,
      artworkBatchRequestSchema.parse(input),
    ),

  /** Every link on this product's GTIN, in `position` order. */
  links: (productId: string) => query("links.list", linkListSchema, { productId }),

  /**
   * The GS1 Web Vocabulary snapshot this platform serves.
   *
   * Read so the type picker can decline to offer a type the API would refuse — the three
   * GS1 restricts to the resolver carry `isDefaultEligible: false`. A screen with its own
   * list of those three would be a fourth copy of a fact the registry owns and the
   * snapshot is refreshed by release.
   */
  linkTypes: () => query("linkTypes.list", linkTypeListSchema),

  /**
   * **The merge preview — the real merge, never a client-side guess.**
   *
   * `acceptance-tests.md` requires it in as many words, and the mechanical half is a
   * dependency-cruiser rule: `apps/app` may not import `@packagepage/gs1`, so there is no
   * merge in this bundle to guess with. Every question the simulator answers — where a scan
   * lands, which link holds a grain's slot, whether a recall is what a scanner sees — is
   * answered by the server running the same functions the resolver runs.
   *
   * The input is parsed with `linksetPreviewQuerySchema` **on the way out**, so the
   * coercions the server would apply (an empty `lot` means "not present in the scanned
   * code") are applied by the same definition before the request is made.
   */
  linksetPreview: (productId: string, input: Partial<LinksetPreviewQuery> = {}) =>
    query("links.preview", linksetPreviewSchema, {
      productId,
      ...linksetPreviewQuerySchema.parse(input),
    }),

  /**
   * One recall, its trail, and **what it suppressed**.
   *
   * `restore.suppressed` is the field link management joins on to say *"suspended by the
   * recall of …"*. Until c35 no response carried it, so a user's own default appeared
   * silently unflagged — the state §1.3 exists to stop the screen showing.
   */
  recall: (recallId: string) => query("recalls.read", recallDetailSchema, { recallId }),

  /**
   * Publish a recall notice. The body is sent raw: the op runs its staged schemas in R6's
   * order, and `recall-flow.ts`'s `preflight` applies the same stages first.
   */
  publishRecall: (body: Record<string, unknown>) =>
    mutate("recalls.publish", body, recallPublishedSchema),

  /** Widen only. `body` is the whole scope after the change, sent raw like the publish body. */
  widenRecall: (recallId: string, body: Record<string, unknown>) =>
    mutate("recalls.widen", { recallId, body }, recallWidenedSchema),

  /** Admins and owners. Any other role gets `FORBIDDEN` with its `detail`, which the screen renders. */
  liftRecall: (recallId: string) =>
    mutate("recalls.lift", { recallId }, recallLiftedSchema),

  createLink: (productId: string, link: CreateLinkInput) =>
    mutate(
      "links.create",
      { productId, link: createLinkSchema.parse(link) },
      linkCreatedSchema,
    ),

  updateLink: (linkId: string, patch: UpdateLinkInput) =>
    mutate(
      "links.update",
      { linkId, patch: updateLinkSchema.parse(patch) },
      linkUpdatedSchema,
    ),

  /**
   * `activate` and `deactivate` are two calls over one op, and collapsing them would be a
   * defect: §6's matrix gives returning a link to service (`update`) and taking one out of
   * service (`deactivate`) to different roles. A single `setActive(boolean)` would have to
   * pick one, which is a permission decided by a parameter.
   */
  activateLink: (linkId: string) =>
    mutate("links.activate", { linkId }, linkActiveSchema),
  deactivateLink: (linkId: string) =>
    mutate("links.deactivate", { linkId }, linkActiveSchema),

  deleteLink: (linkId: string) => mutate("links.remove", { linkId }, linkDeletedSchema),

  reorderLinks: (productId: string, linkIds: readonly string[]) =>
    mutate("links.reorder", { productId, linkIds }, linkOrderSchema),

  members: () => query("members.list", memberListSchema),
  transferOwnership: (input: { userId: string }) =>
    mutate("members.transferOwner", input, ownershipTransferredSchema),
  setMemberRole: (input: SetMemberRoleInput) =>
    mutate("members.setRole", setMemberRoleSchema.parse(input), memberRoleSetSchema),
  removeMember: (input: { userId: string }) =>
    mutate("members.remove", input, memberRemovedSchema),

  invites: () => query("invites.list", inviteListSchema),
  createInvite: (input: { email: string; role: Role }) =>
    mutate("invites.create", createInviteSchema.parse(input), inviteSummarySchema),
  resendInvite: (input: { id: string }) =>
    mutate("invites.resend", input, inviteSummarySchema),
  revokeInvite: (input: { id: string }) =>
    mutate("invites.revoke", input, inviteRevokedSchema),
  /** Session-only: the org header is what a successful accept lets the SPA set. */
  acceptInvite: (token: string) =>
    mutate("invites.accept", { token }, inviteAcceptedSchema),

  /** Newest first; the history screen reads one page. */
  imports: () => query("imports.list", importPageSchema, importListQuerySchema.parse({})),
  importDetail: (id: string) =>
    query("imports.get", importDetailSchema, importIdSchema.parse({ id })),
  /** A presigned PUT. The bytes go to the bucket, never here. */
  createImport: (input: CreateImportInput) =>
    mutate("imports.create", createImportSchema.parse(input), importUploadSchema),
  markImportUploaded: (id: string) =>
    mutate("imports.uploaded", importIdSchema.parse({ id }), importViewSchema),
  cancelImport: (id: string) =>
    mutate("imports.cancel", importIdSchema.parse({ id }), importViewSchema),
  confirmImportMapping: (input: ConfirmImportMappingInput & { id: string }) =>
    mutate(
      "imports.confirmMapping",
      confirmImportMappingSchema.extend(importIdSchema.shape).parse(input),
      importViewSchema,
    ),
  applyImportRows: (input: ApplyImportInput & { id: string }) =>
    mutate(
      "imports.applyRows",
      applyImportSchema.extend(importIdSchema.shape).parse(input),
      importViewSchema,
    ),
  importRows: (input: Partial<ImportRowsQuery> & { id: string }) =>
    query(
      "imports.rows",
      importRowsPageSchema,
      importRowsQuerySchema.extend(importIdSchema.shape).parse(input),
    ),
  /** `rows: "failed"` is the rows apply refused; leaving it out is the rows that did not validate. */
  importReport: (input: { id: string; rows?: "failed" }) =>
    query(
      "imports.report",
      importReportSchema,
      importReportQuerySchema.extend(importIdSchema.shape).parse(input),
    ),
} as const;
